// Ed25519 identity keys (docs/03 §2). The identity is a 32-byte seed: it is
// what the BIP39 recovery phrase encodes, what the split-KDF blob encrypts,
// and what all signatures derive from.
//
// WebCrypto cannot derive an Ed25519 public key from caller-supplied seed
// material (importKey('pkcs8') yields only a private CryptoKey), so public-key
// derivation always uses @noble/curves. Signing prefers WebCrypto Ed25519
// (platform code, constant-time, key never touches JS after import) and falls
// back to @noble on runtimes without Ed25519 support (e.g. older Safari).

import { ed25519 } from '@noble/curves/ed25519.js';
import { concatBytes, fromHex, randomBytes, toBase64 } from '../lib/bytes.js';

export const SEED_LENGTH = 32;

/** DER PKCS#8 prefix for a raw Ed25519 seed (RFC 8410). */
const PKCS8_PREFIX = fromHex('302e020100300506032b657004220420');
/** DER SPKI prefix for a raw Ed25519 public key. */
const SPKI_PREFIX = fromHex('302a300506032b6570032100');

export interface IdentityKeys {
  /** 32-byte Ed25519 seed. SECRET — lives only in the keystore. */
  seed: Uint8Array;
  /** 32-byte raw public key. */
  publicKey: Uint8Array;
  /** Wire encoding (protocol `Ed25519PublicKey`: 44 base64 chars). */
  publicKeyB64: string;
}

export function keysFromSeed(seed: Uint8Array): IdentityKeys {
  if (seed.length !== SEED_LENGTH) {
    throw new Error(`identity seed must be ${SEED_LENGTH} bytes`);
  }
  const publicKey = ed25519.getPublicKey(seed);
  return { seed, publicKey, publicKeyB64: toBase64(publicKey) };
}

export function generateIdentity(): IdentityKeys {
  return keysFromSeed(randomBytes(SEED_LENGTH));
}

// WebCrypto Ed25519 support is probed once and cached.
let webCryptoSupport: Promise<boolean> | undefined;

function supportsWebCryptoEd25519(): Promise<boolean> {
  webCryptoSupport ??= (async () => {
    try {
      const probe = concatBytes(PKCS8_PREFIX, new Uint8Array(SEED_LENGTH));
      await crypto.subtle.importKey('pkcs8', probe as BufferSource, { name: 'Ed25519' }, false, [
        'sign',
      ]);
      return true;
    } catch {
      return false;
    }
  })();
  return webCryptoSupport;
}

/** Test seam: forget the cached WebCrypto probe result. */
export function resetWebCryptoProbeForTests(): void {
  webCryptoSupport = undefined;
}

export async function signWithIdentity(
  seed: Uint8Array,
  message: Uint8Array,
): Promise<Uint8Array> {
  if (seed.length !== SEED_LENGTH) {
    throw new Error(`identity seed must be ${SEED_LENGTH} bytes`);
  }
  if (await supportsWebCryptoEd25519()) {
    try {
      const key = await crypto.subtle.importKey(
        'pkcs8',
        concatBytes(PKCS8_PREFIX, seed) as BufferSource,
        { name: 'Ed25519' },
        false,
        ['sign'],
      );
      return new Uint8Array(await crypto.subtle.sign('Ed25519', key, message as BufferSource));
    } catch {
      // Fall through to @noble — never fail a signature over a platform quirk.
    }
  }
  return ed25519.sign(message, seed);
}

export async function verifySignature(
  publicKey: Uint8Array,
  message: Uint8Array,
  signature: Uint8Array,
): Promise<boolean> {
  if (await supportsWebCryptoEd25519()) {
    try {
      const key = await crypto.subtle.importKey(
        'spki',
        concatBytes(SPKI_PREFIX, publicKey) as BufferSource,
        { name: 'Ed25519' },
        false,
        ['verify'],
      );
      return await crypto.subtle.verify(
        'Ed25519',
        key,
        signature as BufferSource,
        message as BufferSource,
      );
    } catch {
      // Fall through to @noble.
    }
  }
  try {
    return ed25519.verify(signature, message, publicKey);
  } catch {
    return false;
  }
}
